<template>
  <div class="results-layer" :class="{ 'is-visible': isVisible }">
    <p
      v-if="firstCountryCode && locationCountryCode && locationCountryCode !== firstCountryCode"
      class="other-country-info">
      {{
        translations.otherCountryInfo.replace(
          '{country}',
          sbCountryCodeToLabelMap[firstCountryCode] ?? translations.differentCountry
        )
      }}
    </p>
    <div ref="resultsContainer" class="results-container">
      <div class="fade is-top" :class="{ 'is-hidden': arrivedState.top }"></div>
      <template v-for="(result, index) in visibleEntries" :key="result.store.uuid">
        <SharedEMMapResult
          :store="result.store"
          :isSelected="selectedStore?.uuid === result.store.uuid"
          :buttonLink="buttonLink"
          :storeContactType="storeContactType"
          :brand="brand"
          @click="emit('storeSelected', result.store)">
          <template #contact-form-button><slot name="contact-form-button"></slot></template>
        </SharedEMMapResult>
        <hr
          v-if="brand === Brand.Schaerer && index < visibleEntries.length - 1"
          class="line"
          :class="{ 'is-hidden': selectedStoreIndex === index || selectedStoreIndex === index + 1 }" />
      </template>
      <div class="fade is-bottom" :class="{ 'is-hidden': arrivedState.bottom }"></div>
    </div>
    <div v-if="!isTablet && results.length > visibleEntriesCount" class="mobile-button-container">
      <slot name="load-more-button"> </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap';
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { useScroll } from '@vueuse/core';
import { useBreakpoint } from '@seb-platform/shared/composables';
import { sbCountryCodeToLabelMap, type SbCountryCode } from '@seb-platform/shared/types/google-maps';
import type { StoryblokMultilink } from '../../../../types/storyblok';
import { Brand, SebEvent } from '../../../../types/utils';
import type { EMMapTranslations, SortedStore, Store } from './SharedEMMap.types';
import SharedEMMapResult from './SharedEMMapResult.vue';

const props = defineProps<{
  results: SortedStore[];
  locationCountryCode?: SbCountryCode;
  buttonLink?: Exclude<StoryblokMultilink, { linktype?: 'email' } | { linktype?: 'asset' }>;
  storeContactType: '' | 'button' | 'list' | 'both';
  selectedStore: Store | null;
  translations: EMMapTranslations;
  brand: Brand;
  isVisible: boolean;
}>();

const emit = defineEmits<{
  (e: 'storeSelected', store: Store): void;
}>();

const visibleEntriesCount = ref(4);
const visibleEntries = computed(() => (isTablet ? props.results : props.results.slice(0, visibleEntriesCount.value)));
const firstCountryCode = computed(() => visibleEntries.value?.[0]?.store.content.location?.country);
const selectedStoreIndex = computed(() =>
  props.results.findIndex(result => result.store.uuid === props.selectedStore?.uuid)
);
const $resultsContainer = useTemplateRef('resultsContainer');

const {
  state: { isTablet },
} = useBreakpoint();
const { arrivedState } = useScroll($resultsContainer, {
  throttle: 100,
  offset: { top: 20, bottom: 20 },
});

watch(
  () => props.results,
  () => (visibleEntriesCount.value = 4)
);

function handleLoadMore() {
  if (!$resultsContainer.value) {
    return;
  }
  const elHeight = $resultsContainer.value?.scrollHeight ?? 0;
  visibleEntriesCount.value += 4;
  gsap.fromTo($resultsContainer.value, { height: elHeight }, { height: 'auto', duration: 0.3, ease: 'ease-in-out' });
}

onMounted(() => {
  document.addEventListener(SebEvent.EmMapLoadMore, handleLoadMore);
});

onUnmounted(() => {
  document.removeEventListener(SebEvent.EmMapLoadMore, handleLoadMore);
});
</script>

<style src="./SharedEMMapResultsLayer.scss" lang="scss" scoped />
